import { Checkbox } from "@chakra-ui/react"
import { useMemo, useState } from "react"

// Reusable hook to manage row selection by id
export function useRowSelection(initial?: Iterable<string>) {
  const [selected, setSelected] = useState<Set<string>>(new Set(initial))

  const isSelected = (id: string) => selected.has(id)

  const toggleOne = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const getHeaderState = (visibleIds: string[]) => {
    const count = visibleIds.reduce(
      (acc, id) => acc + (selected.has(id) ? 1 : 0),
      0,
    )
    const all = visibleIds.length > 0 && count === visibleIds.length
    const some = count > 0 && !all
    return { all, some }
  }

  const toggleSelectAllVisible = (visibleIds: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) {
        visibleIds.forEach((id) => {
          next.add(id)
        })
      } else {
        visibleIds.forEach((id) => {
          next.delete(id)
        })
      }
      return next
    })
  }

  const selectedIds = useMemo(() => Array.from(selected), [selected])

  return {
    selected,
    selectedIds,
    setSelected,
    isSelected,
    toggleOne,
    getHeaderState,
    toggleSelectAllVisible,
  } as const
}

// Header checkbox component: controls select-all for current visible ids
export function SelectAllCheckbox(props: {
  visibleIds: string[]
  selection: ReturnType<typeof useRowSelection>
}) {
  const { visibleIds, selection } = props
  const { all, some } = selection.getHeaderState(visibleIds)

  return (
    <Checkbox
      isChecked={all}
      isIndeterminate={some}
      onChange={(e) =>
        selection.toggleSelectAllVisible(visibleIds, e.target.checked)
      }
    />
  )
}

// Row checkbox component: controls selection of a single row id
export function RowCheckbox(props: {
  id: string
  selection: ReturnType<typeof useRowSelection>
}) {
  const { id, selection } = props
  return (
    <Checkbox
      isChecked={selection.isSelected(id)}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => selection.toggleOne(id, e.target.checked)}
    />
  )
}
